import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { church } from "@/config/church";
import useNoIndex from "@/hooks/useNoIndex";
import { LogIn, Lock, Mail } from "lucide-react";

export default function AdminLogin() {
  useNoIndex();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    try {
      setBusy(true);
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back, pastor.");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-npbc-black text-white grid place-items-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 opacity-40">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-npbc-red/40 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-npbc-gold/25 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8" data-testid="admin-login-logo">
          <img src={church.logo} alt="NPBC" className="h-14 w-14 object-contain" />
          <div className="leading-tight">
            <div className="heading-serif text-lg">New Philadelphia</div>
            <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-npbc-gold">Church Admin</div>
          </div>
        </Link>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 shadow-2xl"
        >
          <div className="eyebrow !text-npbc-gold">Sign In</div>
          <h1 className="mt-3 heading-serif text-3xl">Welcome, servant of the Lord.</h1>
          <p className="mt-2 text-white/60 text-sm">Sign in to manage church events, sermons, announcements, and messages.</p>

          <div className="mt-8 space-y-4">
            <label className="block">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-npbc-gold mb-2">Email</div>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email"
                  placeholder="pastor@newphiladelphiabaptist.org"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.04] pl-11 pr-4 py-3 text-sm outline-none placeholder:text-white/40 focus:border-npbc-gold"
                />
              </div>
            </label>
            <label className="block">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-npbc-gold mb-2">Password</div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.04] pl-11 pr-4 py-3 text-sm outline-none placeholder:text-white/40 focus:border-npbc-gold"
                />
              </div>
            </label>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-gold mt-8 w-full justify-center disabled:opacity-60"
          >
            {busy ? "Signing in..." : (<> <LogIn className="h-4 w-4" /> Sign in </>)}
          </button>

          <div className="mt-6 text-center text-xs text-white/50">
            <Link to="/" className="hover:text-white transition">← Back to church website</Link>
          </div>
        </form>
      </div>

      <Toaster position="top-center" richColors />
    </div>
  );
}
